(() => {
  if (window.sylavieGarmentCareControllerLoaded) return;

  window.sylavieGarmentCareControllerLoaded = true;

  const SECTION_SELECTOR = '[data-garment-care]';
  const REVEAL_SELECTOR = '[data-garment-care-reveal]';
  const PRINCIPLE_SELECTOR = '.garment-care__principle';
  const GUIDE_ROW_SELECTOR = '.garment-care__guide-item';
  const SYMBOL_SELECTOR = '.garment-care__symbol';
  const VISIBLE_CLASS = 'is-visible';
  const INITIALIZED_CLASS = 'is-initialized';
  const PRINCIPLE_STAGGER = 80;
  const GUIDE_ROW_STAGGER = 55;
  const SYMBOL_STAGGER = 70;
  const instances = new WeakMap();

  class GarmentCareSection {
    constructor(section) {
      this.section = section;
      this.items = Array.from(section.querySelectorAll(REVEAL_SELECTOR));
      this.principles = Array.from(section.querySelectorAll(PRINCIPLE_SELECTOR));
      this.guideRows = Array.from(section.querySelectorAll(GUIDE_ROW_SELECTOR));
      this.symbols = Array.from(section.querySelectorAll(SYMBOL_SELECTOR));
      this.observer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => this.refresh();

      this.addMotionListener();
      this.setStagger();
      this.refresh();
    }

    setStagger() {
      this.setGroupStagger(this.principles, PRINCIPLE_STAGGER);
      this.setGroupStagger(this.guideRows, GUIDE_ROW_STAGGER);
      this.setGroupStagger(this.symbols, SYMBOL_STAGGER);
    }

    setGroupStagger(items, step) {
      items.forEach((item, index) => {
        item.style.setProperty('--garment-care-reveal-delay', `${index * step}ms`);
      });
    }

    addMotionListener() {
      if (this.reduceMotion.addEventListener) {
        this.reduceMotion.addEventListener('change', this.onMotionChange);
        return;
      }

      this.reduceMotion.addListener(this.onMotionChange);
    }

    removeMotionListener() {
      if (this.reduceMotion.removeEventListener) {
        this.reduceMotion.removeEventListener('change', this.onMotionChange);
        return;
      }

      this.reduceMotion.removeListener(this.onMotionChange);
    }

    refresh() {
      this.disconnect();

      if (this.reduceMotion.matches || !('IntersectionObserver' in window)) {
        this.section.classList.remove(INITIALIZED_CLASS);
        this.items.forEach((item) => item.classList.add(VISIBLE_CLASS));
        return;
      }

      this.section.classList.add(INITIALIZED_CLASS);
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(VISIBLE_CLASS);
            this.observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -6% 0px',
          threshold: 0.08,
        }
      );

      this.items.forEach((item) => this.observer.observe(item));
    }

    disconnect() {
      if (!this.observer) return;

      this.observer.disconnect();
      this.observer = null;
    }

    destroy() {
      this.disconnect();
      this.removeMotionListener();
      this.section.classList.remove(INITIALIZED_CLASS);
    }
  }

  const getSections = (root = document) => {
    if (root.matches?.(SECTION_SELECTOR)) return [root];

    return Array.from(root.querySelectorAll(SECTION_SELECTOR));
  };

  const initSections = (root = document) => {
    getSections(root).forEach((section) => {
      const existing = instances.get(section);
      if (existing) existing.destroy();

      instances.set(section, new GarmentCareSection(section));
    });
  };

  const destroySections = (root) => {
    getSections(root).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections());
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySections(event.target));
})();
